import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, AlertCircle, ArrowRight, Settings } from 'lucide-react';
import ClientAuthService from '../services/clientAuthService';

const MandateSuccessPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [mandateDetails, setMandateDetails] = useState(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const redirectFlowId = urlParams.get('redirect_flow_id');
    
    if (redirectFlowId) {
      completeMandateSetup(redirectFlowId);
    } else {
      setError('Missing redirect flow ID');
      setLoading(false);
    }
  }, [location]);

  const completeMandateSetup = async (redirectFlowId) => {
    try {
      setLoading(true);
      setError('');
      
      const response = await ClientAuthService.completeMandateSetup(redirectFlowId);
      
      if (response.success) {
        setSuccess(true);
        setMandateDetails(response.data);
        
        // Redirect to dashboard with payments tab after 5 seconds
        setTimeout(() => {
          navigate('/client/dashboard?tab=payments');
        }, 5000);
      } else {
        setError(response.message || 'Failed to complete mandate setup');
      }
    } catch (error) {
      console.error('Error completing mandate setup:', error);
      setError('Failed to complete payment method setup');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="card">
              <div className="card-body text-center py-5">
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <h5>Setting up your payment method...</h5>
                <p className="text-muted">Please wait while we complete the setup process.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid">
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="card">
              <div className="card-body text-center py-5">
                <AlertCircle size={64} className="text-danger mb-3" />
                <h5 className="text-danger">Setup Failed</h5>
                <p className="text-muted mb-4">{error}</p>
                <div className="d-flex justify-content-center gap-2">
                  <button
                    className="btn btn-primary"
                    onClick={() => navigate('/client/dashboard?tab=payments')}
                  >
                    <Settings size={16} className="me-1" />
                    Go to Payment Methods
                  </button>
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/client/dashboard')}
                  >
                    Back to Dashboard
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="container-fluid">
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="card">
              <div className="card-body text-center py-5">
                <CheckCircle size={64} className="text-success mb-3" />
                <h5 className="text-success">Payment Method Added Successfully!</h5>
                <p className="text-muted mb-4">
                  Your Direct Debit mandate has been set up successfully. You can now use this payment method for:
                </p>
                
                <div className="row text-start mb-4">
                  <div className="col-md-6">
                    <ul className="list-unstyled">
                      <li>
                        <CheckCircle size={16} className="text-success me-2" />
                        Automatic invoice payments
                      </li>
                      <li>
                        <CheckCircle size={16} className="text-success me-2" />
                        Wallet top-ups
                      </li>
                    </ul>
                  </div>
                  <div className="col-md-6">
                    <ul className="list-unstyled">
                      <li>
                        <CheckCircle size={16} className="text-success me-2" />
                        Recurring payments
                      </li>
                      <li>
                        <CheckCircle size={16} className="text-success me-2" />
                        Direct Debit protection
                      </li>
                    </ul>
                  </div>
                </div>

                {mandateDetails && (
                  <div className="alert alert-info text-start mb-4">
                    <h6>Payment Method Details:</h6>
                    <ul className="mb-0">
                      <li><strong>Mandate ID:</strong> {mandateDetails.mandate_id}</li>
                      <li><strong>Customer ID:</strong> {mandateDetails.customer_id}</li>
                      <li><strong>Status:</strong> Active</li>
                    </ul>
                  </div>
                )}

                <div className="d-flex justify-content-center gap-2">
                  <button
                    className="btn btn-primary"
                    onClick={() => navigate('/client/dashboard?tab=payments')}
                  >
                    <Settings size={16} className="me-1" />
                    Manage Payment Methods
                  </button>
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/client/dashboard')}
                  >
                    Back to Dashboard
                  </button>
                </div>

                <div className="mt-4">
                  <small className="text-muted">
                    <ArrowRight size={14} className="me-1" />
                    You will be redirected to settings in a few seconds...
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default MandateSuccessPage;